/**
 * What a SessionStartModal is made of.
 *
 * The action list is the whole point: the three screens this modal replaced
 * differed only in their buttons, so the buttons became data and the modal
 * itself stopped knowing which route it serves.
 */

import type { RefObject } from "react";
import type { useForm } from "@/features/tutor/context/FormContext";
import type { RetakeAssessmentItem } from "@/features/tutor/vo-demo/contracts/retakeAssessments";

export type SessionStartActionId =
  | "start"
  | "resume"
  | "restart_bite"
  | "restart_lesson";

export interface SessionStartAction {
  id: SessionStartActionId;
  label: string;
  /** Shown with a spinner while `onSelect` is in flight. */
  pendingLabel?: string;
  /**
   * `primary` and `secondary` render as buttons in a row, in the order given;
   * `link` renders as a text link underneath them.
   */
  kind: "primary" | "secondary" | "link";
  onSelect: () => void | Promise<void>;
  /**
   * When set, the action is shown but refused, and this is the sentence the
   * student reads instead. A reason rather than a boolean because the only
   * caller of it -- resuming a lesson begun in another language -- has to say
   * which language, and a screen that greys out a button without saying why
   * is a screen with a bug in it.
   */
  disabledReason?: string | null;
  /** When set, the action runs only after the student confirms. */
  confirm?: {
    title: string;
    message: string;
    confirmLabel: string;
  };
}

/**
 * Refuse the resume action while the session's language differs from the one
 * selected, and say why.
 *
 * Returns the list unchanged when nothing is locked, so a caller can pass the
 * result straight through without checking.
 */
export const applyResumeLanguageLock = (
  actions: SessionStartAction[],
  {
    locked,
    sessionLanguageLabel,
    selectedLanguageLabel,
  }: {
    locked: boolean;
    sessionLanguageLabel: string | null;
    selectedLanguageLabel: string | null;
  },
): SessionStartAction[] => {
  if (!locked) return actions;
  const reason = sessionLanguageLabel
    ? `This lesson was in progress in ${sessionLanguageLabel}. Switch back to ${sessionLanguageLabel} to resume, or restart the bite or lesson to continue in ${selectedLanguageLabel ?? "the new language"}.`
    : "This lesson was in progress in another language. Switch back to it to resume, or restart the bite or lesson to continue.";
  return actions.map((action) =>
    action.id === "resume" ? { ...action, disabledReason: reason } : action,
  );
};

export interface SessionStartModalProps {
  isOpen: boolean;
  onClose: () => void;
  actions?: SessionStartAction[];
  retakeItems?: RetakeAssessmentItem[];
  /** The language this lesson's session was begun in, if it recorded one. */
  sessionLanguage?: string | null;
  /** Keys the once-per-lesson language seed. */
  lessonId?: number | string | null;
  /**
   * Awaited like an action: the caller's handler closes this modal itself, but
   * only after a round trip, and the screen must stay locked until then.
   */
  onRetakeAssessment?: (type: string) => void | Promise<void>;
}

export interface SharedProps {
  currentLessonName: string | null;
  formData: ReturnType<typeof useForm>["formData"];
  actions: SessionStartAction[];
  submitting: boolean;
  pendingActionId: SessionStartActionId | null;
  runAction: (action: SessionStartAction) => void;
  retakeItems: RetakeAssessmentItem[];
  retakeDropdownOpen: boolean;
  setRetakeDropdownOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  voicePreviewRef: RefObject<any>;
  handleProgress: (progress: number) => void;
  handlePlayStateChange: (playing: boolean) => void;
  openChangeLanguage: () => void;
  openChangeCharacter: () => void;
  openChangeVoice: () => void;
  handleClose: () => void;
  runRetake?: (type: string) => void;
  isVoiceTrained: boolean;
  handleSpeakName: () => void;
}
